import React, { useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SecurityIcon from '@mui/icons-material/Security';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import BookingFlow from '../components/BookingFlow';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const HeroSection = styled(Box)(({ theme }) => ({
  backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(/images/kamran-abdullayev-9V1cYW4JIfQ-unsplash.jpg)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.common.white,
  position: 'relative',
  textAlign: 'center',
  [theme.breakpoints.down('md')]: {
    minHeight: '90vh',
  },
}));

const Feature = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: 'left',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  borderLeft: '3px solid transparent',
  '&:hover': {
    borderLeftColor: '#D4AF37',
    paddingLeft: theme.spacing(5),
    backgroundColor: 'rgba(212, 175, 55, 0.05)',
  },
}));

const features = [
  {
    icon: <DirectionsCarIcon sx={{ fontSize: 48 }} />,
    title: 'Mercedes V-Class Fleet',
    description: 'Experience ultimate comfort with our exclusive fleet of luxury Mercedes V-Class vehicles.',
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 48 }} />,
    title: 'Professional Chauffeurs',
    description: 'Our highly trained chauffeurs provide a discreet, reliable, and professional service.',
  },
  {
    icon: <SupportAgentIcon sx={{ fontSize: 48 }} />,
    title: 'Dedicated Service',
    description: 'Personal attention to every detail of your journey, available 24/7.',
  },
];

const HomePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const bookingWidgetRef = useRef(null);

  const handleBookingClick = () => {
    if (bookingWidgetRef.current) {
      bookingWidgetRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  return (
    <Box>
      <HeroSection>
        <Container maxWidth="lg">
          <Box 
            py={{ xs: 8, md: 12 }}
            sx={{
              backdropFilter: 'blur(2px)',
              padding: { xs: 4, md: 6 },
              borderRadius: 0,
            }}
          >
            <Box
              sx={{
                maxWidth: { xs: '250px', sm: '350px', md: '450px' },
                width: '100%',
                height: { xs: '150px', sm: '200px', md: '250px' },
                mb: 4,
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
              }}
            >
              <Box
                component="img"
                src="/images/sls-logo.png"
                alt="SLS Luxury Services"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  objectPosition: 'center',
                  filter: 'drop-shadow(0 4px 20px rgba(0, 0, 0, 0.9))',
                }}
              />
            </Box>
            <Typography 
              variant="h2"
              sx={{ 
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                fontWeight: 300,
                letterSpacing: '0.1em',
                mb: 7,
                color: '#FFFFFF',
                lineHeight: 1.4,
                textTransform: 'uppercase',
                textShadow: '0 2px 12px rgba(0, 0, 0, 0.9)',
              }}
            >
              Serene Luxury Services
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              gap: 3,
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'center',
              alignItems: 'center',
            }}>
              <Button
                onClick={handleBookingClick}
                variant="contained"
                size="large"
                sx={{
                  bgcolor: '#D4AF37',
                  color: '#000000',
                  fontWeight: 700,
                  py: 3,
                  px: 8,
                  fontSize: '1.1rem',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  boxShadow: '0 4px 20px rgba(212, 175, 55, 0.4)',
                  '&:hover': {
                    bgcolor: '#F4D03F',
                    transform: 'translateY(-4px)',
                    boxShadow: '0 16px 40px rgba(212, 175, 55, 0.6)',
                  },
                  minWidth: 240,
                }}
              >
                Reserve A Luxury V Class
              </Button>
            </Box>
          </Box>
        </Container>
      </HeroSection>

      {/* Booking Section - Full Width */}
      <Box 
        ref={bookingWidgetRef}
        sx={{ 
          bgcolor: '#0A0A0A',
          py: { xs: 8, md: 12 },
          scrollMarginTop: { xs: '64px', md: '72px' },
          borderTop: '1px solid rgba(212, 175, 55, 0.2)',
          borderBottom: '1px solid rgba(212, 175, 55, 0.2)',
        }}
      >
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            textAlign="center" 
            sx={{ 
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 800,
              mb: 2,
              letterSpacing: '0.05em',
              color: '#FFFFFF',
              textTransform: 'uppercase',
            }}
          >
            Luxury V Class Chauffeur
          </Typography>
          <Box sx={{ 
            width: 60, 
            height: 3, 
            bgcolor: '#D4AF37', 
            mx: 'auto', 
            mb: 8 
          }} />
          <Box
            sx={{
              background: '#121212',
              border: '1px solid rgba(212, 175, 55, 0.3)',
              p: { xs: 4, md: 6 },
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.8)',
            }}
          >
            <BookingFlow variant="home" />
          </Box>
        </Container>
      </Box>

      {/* Features - Split Layout */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 14 } }}>
        <Grid container spacing={8} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography 
              variant="overline"
              sx={{ 
                fontSize: '0.875rem',
                fontWeight: 600,
                letterSpacing: '0.15em',
                mb: 2,
                color: 'text.secondary',
              }}
            >
              Why Choose SLS
            </Typography>
            <Typography 
              variant="h2" 
              sx={{ 
                fontSize: { xs: '2rem', md: '3rem' },
                fontWeight: 700,
                letterSpacing: '-0.02em',
                mb: 3,
                lineHeight: 1.1,
              }}
            >
              Excellence in every detail
            </Typography>
            <Typography 
              variant="body1"
              sx={{ 
                fontSize: '1.1rem',
                color: 'text.secondary',
                lineHeight: 1.8,
                mb: 4,
              }}
            >
              From our professional chauffeurs to our premium Mercedes V-Class fleet, we deliver an unparalleled luxury experience.
            </Typography>
            <Button
              component={RouterLink}
              to="/services"
              variant="outlined"
              size="large"
              sx={{
                borderColor: '#000000',
                color: '#000000',
                fontWeight: 600,
                py: 1.5,
                px: 4,
              }}
            >
              Learn More
            </Button>
          </Grid>
          <Grid item xs={12} md={6}>
            {features.map((feature, index) => (
              <Feature key={index}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box sx={{ color: '#000000', mt: 0.5 }}>
                    {feature.icon}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600,
                        mb: 1,
                        fontSize: '1.25rem',
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography 
                      color="text.secondary"
                      sx={{ fontSize: '1rem', lineHeight: 1.6 }}
                    >
                      {feature.description}
                    </Typography>
                  </Box>
                </Box>
              </Feature>
            ))}
          </Grid>
        </Grid>
      </Container>

      {/* Services - Horizontal Cards */}
      <Box sx={{ bgcolor: '#000000', color: '#FFFFFF', py: { xs: 8, md: 14 } }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h2" 
            textAlign="center" 
            sx={{ 
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 700,
              mb: 8,
              letterSpacing: '-0.02em',
            }}
          >
            Our Services
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: 2,
                  height: 400,
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.02)',
                  },
                }}
              >
                <Box
                  component="img"
                  src="/images/the-whiteley-penthouse-1.jpg"
                  alt="Short Stay Apartments"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(to top, rgba(0,0,0,0.9), transparent)',
                    p: 4,
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    Short Stay Apartments
                  </Typography>
                  <Typography sx={{ fontSize: '0.95rem', opacity: 0.9, mb: 2 }}>
                    Luxury serviced apartments for your stay
                  </Typography>
                  <Button
                    href="mailto:info@sls-services.com?subject=Short Stay Apartment Enquiry"
                    variant="text"
                    sx={{
                      color: '#FFFFFF',
                      fontWeight: 600,
                      p: 0,
                      '&:hover': {
                        bgcolor: 'transparent',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Make Enquiry →
                  </Button>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: 2,
                  height: 400,
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.02)',
                  },
                }}
              >
                <Box
                  component="img"
                  src="/images/services/airport-transfer.jpg"
                  alt="Luxury V Class Chauffeur"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(to top, rgba(0,0,0,0.9), transparent)',
                    p: 4,
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    Luxury V Class Chauffeur
                  </Typography>
                  <Typography sx={{ fontSize: '0.95rem', opacity: 0.9, mb: 2 }}>
                    Premium Mercedes V-Class chauffeur service
                  </Typography>
                  <Button
                    component={RouterLink}
                    to="/booking"
                    variant="text"
                    sx={{
                      color: '#FFFFFF',
                      fontWeight: 600,
                      p: 0,
                      '&:hover': {
                        bgcolor: 'transparent',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Book Now →
                  </Button>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: 2,
                  height: 400,
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.02)',
                  },
                }}
              >
                <Box
                  component="img"
                  src="https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?w=800&auto=format&fit=crop"
                  alt="Sporting and Entertainment Events"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(to top, rgba(0,0,0,0.9), transparent)',
                    p: 4,
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    Sporting & Entertainment Events
                  </Typography>
                  <Typography sx={{ fontSize: '0.95rem', opacity: 0.9, mb: 2 }}>
                    VIP transport to concerts, sports, and shows
                  </Typography>
                  <Button
                    href="mailto:info@sls-services.com?subject=Event Transport Enquiry"
                    variant="text"
                    sx={{
                      color: '#FFFFFF',
                      fontWeight: 600,
                      p: 0,
                      '&:hover': {
                        bgcolor: 'transparent',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Make Enquiry →
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage; 